import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Mail, Loader2, AlertCircle } from "lucide-react";
import { SiGoogle, SiFacebook } from "react-icons/si";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage("");

    // Simulate network delay for premium feel
    await new Promise(resolve => setTimeout(resolve, 1000));

    if (email === "example@example.com" && password === "password") {
      // Handle Success (e.g., set token, redirect)
      setIsLoading(false);
    } else {
      setErrorMessage("Invalid credentials. Please verify your email and password.");
      setIsLoading(false);
    }
  };

  const inputBaseClass = "w-full pl-11 pr-4 py-3.5 rounded-xl border focus:outline-none focus:ring-1 focus:ring-emerald-500 transition-all duration-200 bg-zinc-50 dark:bg-[#09090B] border-zinc-200 dark:border-white/10 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-600";

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[#FAFAFA] dark:bg-[#09090B] relative overflow-hidden pt-16">
      
      {/* Subtle Ambient Background */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg h-[400px] bg-emerald-500/5 dark:bg-emerald-500/10 blur-[100px] pointer-events-none rounded-full" />

      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md relative z-10"
      >
        <div className="p-8 sm:p-10 rounded-3xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-white/10 shadow-xl shadow-zinc-200/20 dark:shadow-none">
          
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-zinc-100 dark:bg-white/5 border border-zinc-200 dark:border-white/10 mb-6">
              <Lock className="w-6 h-6 text-zinc-900 dark:text-white" />
            </div>
            <h2 className="text-3xl font-display font-semibold text-zinc-900 dark:text-white tracking-tight mb-2">
              Welcome Back
            </h2>
            <p className="text-zinc-500 dark:text-zinc-400 text-sm">
              Authenticate to access the admin dashboard
            </p>
          </div>

          <AnimatePresence mode="wait">
            {errorMessage && (
              <motion.div 
                initial={{ opacity: 0, height: 0, marginBottom: 0 }} 
                animate={{ opacity: 1, height: "auto", marginBottom: 24 }} 
                exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                className="overflow-hidden"
              >
                <div className="flex items-center p-3.5 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/50 rounded-xl">
                  <AlertCircle className="w-4 h-4 mr-2.5 flex-shrink-0" />
                  {errorMessage}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <input 
                  type="email" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  required 
                  className={inputBaseClass} 
                  placeholder="you@example.com" 
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Password</label>
                <a href="#" className="text-xs font-medium text-emerald-600 dark:text-emerald-400 hover:underline">Forgot password?</a>
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <input 
                  type="password" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  required 
                  className={inputBaseClass} 
                  placeholder="••••••••" 
                />
              </div>
            </div>

            <div className="pt-2">
              <button 
                type="submit"
                disabled={isLoading} 
                className={`w-full flex items-center justify-center py-3.5 rounded-xl text-sm font-medium transition-all duration-300 ${
                  isLoading 
                    ? "bg-zinc-100 text-zinc-400 dark:bg-zinc-800 dark:text-zinc-500 cursor-not-allowed" 
                    : "bg-zinc-900 text-white hover:bg-zinc-800 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white shadow-lg shadow-zinc-900/10 dark:shadow-white/5"
                }`}
              >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
              </button>
            </div>
          </form>

          <div className="mt-8 relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-zinc-200 dark:border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-4 bg-white dark:bg-zinc-900 text-zinc-500 dark:text-zinc-400">
                Or continue with
              </span>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <button 
              type="button"
              className="flex items-center justify-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors bg-zinc-50 text-zinc-700 hover:bg-zinc-100 border border-zinc-200 dark:bg-zinc-800/50 dark:text-zinc-300 dark:border-white/10 dark:hover:bg-zinc-800"
            >
              <SiGoogle className="w-4 h-4 text-[#EA4335]" />
              Google
            </button>
            <button 
              type="button"
              className="flex items-center justify-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors bg-zinc-50 text-zinc-700 hover:bg-zinc-100 border border-zinc-200 dark:bg-zinc-800/50 dark:text-zinc-300 dark:border-white/10 dark:hover:bg-zinc-800"
            >
              <SiFacebook className="w-4 h-4 text-[#1877F2]" />
              Facebook
            </button>
          </div>
          
        </div>
      </motion.div>
    </div>
  );
};

export default Login;